import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Text,
  VStack,
  Badge,
  Spinner,
  Center,
  Heading,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex,
  Icon,
  Divider,
  Checkbox,
  Button,
  useToast,
  HStack,
  Container,
  Card,
  CardBody,
  Avatar,
  Tag,
  IconButton,
  Tooltip,
  Progress,
} from '@chakra-ui/react';
import {
  IoNotificationsOutline,
  IoTimeOutline,
  IoCheckmarkDoneOutline,
  IoRefreshOutline,
  IoPeopleOutline,
} from 'react-icons/io5';
import { FiInbox } from 'react-icons/fi';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import baseUrl from 'Base_Url/baseUrl';

const AllNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const limit = 10;
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');
  const panelBg = useColorModeValue('blue.50', 'blue.900');
  const detailTextColor = useColorModeValue('gray.700', 'gray.200');
  const iconColor = useColorModeValue('blue.500', 'blue.300');
  const titleColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const unreadBg = useColorModeValue('blue.50', 'blue.900');

  const scrollbarTrackColor = useColorModeValue('gray.100', 'gray.700');
  const scrollbarThumbColor = useColorModeValue('gray.400', 'gray.500');
  const scrollbarThumbHoverColor = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUserId(user.id || user._id);
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  }, []);
  const scrollbarStyles = {
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: scrollbarTrackColor,
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: scrollbarThumbColor,
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: scrollbarThumbHoverColor,
    },
  };

  const isNotificationReadByCurrentUser = (notification) => {
    if (!currentUserId || !notification) return true;

    if (
      notification.metadata?.readBy &&
      Array.isArray(notification.metadata.readBy)
    ) {
      return notification.metadata.readBy.some(
        (item) => item.user?.toString() === currentUserId?.toString(),
      );
    }

    return notification.read || false;
  };

  const fetchNotifications = async () => {
    setLoading(!refreshing);
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseUrl}/notifications/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit },
      });

      if (response.data.status) {
        let notificationsData = response?.data?.data?.data || [];

        notificationsData = notificationsData.map((notification) => ({
          ...notification,
          isRead: isNotificationReadByCurrentUser(notification),
          totalRecipients: notification.createdFor?.length || 1,
          readCount: notification.metadata?.readBy?.length || 0,
        }));

        setNotifications(notificationsData);
        setTotalPages(response?.data?.data?.pagination?.totalPages || 1);
        setTotalItems(response?.data?.data?.pagination?.total || 0);
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to fetch notifications',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (currentUserId) {
      fetchNotifications();
    }
  }, [page, currentUserId]);

  const handleRefresh = () => {
    fetchNotifications();
  };

  const handleAccordionChange = (indices) => {
    indices.forEach((index) => {
      const notification = notifications[index];
      if (notification && !notification.isRead) {
        markSingleAsRead(notification._id);
      }
    });
  };

  const markSingleAsRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${baseUrl}/notifications/${id}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status) {
        setNotifications((prev) =>
          prev.map((n) =>
            n._id === id
              ? {
                ...n,
                isRead: true,
                read: true,
                readAt: new Date().toISOString(),
                metadata: {
                  ...n.metadata,
                  readBy: [
                    ...(n.metadata?.readBy || []),
                    { user: currentUserId, readAt: new Date().toISOString() },
                  ],
                },
                readCount: (n.readCount || 0) + 1,
              }
              : n,
          ),
        );
      }
    } catch (err) {
      console.error('Error marking as read', err);
    }
  };

  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === notifications.length) {
      setSelectedIds([]);
    } else {
      const unreadIds = notifications
        .filter((n) => !n.isRead)
        .map((n) => n._id);
      setSelectedIds(unreadIds);
    }
  };

  const markSelectedAsRead = async () => {
    if (selectedIds.length === 0) return;
    setBulkLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${baseUrl}/notifications/mark-all-read`,
        { ids: selectedIds },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status) {
        const data = response.data.data;
        const updatedCount = data.count || selectedIds.length;

        setNotifications((prev) =>
          prev.map((n) =>
            selectedIds.includes(n._id)
              ? {
                ...n,
                isRead: true,
                read: true,
                readAt: new Date().toISOString(),
                metadata: {
                  ...n.metadata,
                  readBy: [
                    ...(n.metadata?.readBy || []),
                    { user: currentUserId, readAt: new Date().toISOString() },
                  ],
                },
                readCount: (n.readCount || 0) + 1,
              }
              : n,
          ),
        );
        setSelectedIds([]);
        toast({
          title: 'Success',
          description: `${updatedCount} notification(s) marked as read`,
          status: 'success',
          duration: 2000,
          position: 'top-right',
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description:
          err.response?.data?.message || 'Failed to update notifications',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBulkLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const selectedCount = selectedIds.length;

  const renderPagination = () => {
    const maxPagesToShow = 5;
    const pages = [];
    const isEllipsisShown = totalPages > maxPagesToShow;

    const createPageButton = (pageNumber) => (
      <Button
        key={pageNumber}
        onClick={() => handlePageChange(pageNumber)}
        colorScheme={page === pageNumber ? 'blue' : 'gray'}
        variant={page === pageNumber ? 'solid' : 'outline'}
        mx="1"
        size="sm"
      >
        {pageNumber}
      </Button>
    );

    if (isEllipsisShown) {
      if (page > Math.ceil(maxPagesToShow / 2)) {
        pages.push(createPageButton(1));
        pages.push(
          <Text key="ellipsis-start" mx="1" fontSize="lg">
            ...
          </Text>,
        );
      }

      const startPage = Math.max(page - Math.floor(maxPagesToShow / 2), 1);
      const endPage = Math.min(
        page + Math.floor(maxPagesToShow / 2),
        totalPages,
      );
      for (let i = startPage; i <= endPage; i++) {
        pages.push(createPageButton(i));
      }

      if (page < totalPages - Math.floor(maxPagesToShow / 2)) {
        pages.push(
          <Text key="ellipsis-end" mx="1" fontSize="lg">
            ...
          </Text>,
        );
        pages.push(createPageButton(totalPages));
      }
    } else {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(createPageButton(i));
      }
    }

    return (
      <Flex justify="center" align="center">
        <IconButton
          aria-label="Previous Page"
          icon={<ChevronLeftIcon />}
          isDisabled={page <= 1}
          onClick={() => handlePageChange(page - 1)}
          mr="2"
          size="sm"
          variant="outline"
        />
        {pages}
        <IconButton
          aria-label="Next Page"
          icon={<ChevronRightIcon />}
          isDisabled={page >= totalPages}
          onClick={() => handlePageChange(page + 1)}
          ml="2"
          size="sm"
          variant="outline"
        />
      </Flex>
    );
  };

  if (loading && !refreshing) {
    return (
      <Center h="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.500">Loading notifications...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Container
      maxW="7xl"
      p={{ base: 4, md: 6 }}
      pt="100px"
      style={{ marginTop: '80px' }}
    >
      {/* Header Section */}
      <Card mb={2} shadow="sm" borderWidth="1px" borderColor={borderColor}>
        <CardBody>
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <VStack align="start" spacing={1}>
              <HStack spacing={3}>
                <Avatar
                  icon={<IoNotificationsOutline />}
                  bg="blue.500"
                  color="white"
                  size="md"
                />
                <Box>
                  <Heading size="lg">Notifications</Heading>
                  <Text color={subtitleColor} fontSize="sm">
                    {totalItems} total • {unreadCount} unread
                  </Text>
                </Box>
              </HStack>
            </VStack>

            <HStack spacing={2}>
              <Badge
                colorScheme="blue"
                variant="subtle"
                px={3}
                py={1}
                borderRadius="full"
              >
                Page {page} of {totalPages}
              </Badge>
              <Tooltip label="Refresh">
                <IconButton
                  icon={<IoRefreshOutline />}
                  onClick={handleRefresh}
                  isLoading={refreshing}
                  variant="ghost"
                  aria-label="Refresh"
                  size="sm"
                />
              </Tooltip>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Bulk Actions Section */}
      {(selectedCount > 0 || notifications.length > 0) && (
        <Card mb={2} shadow="lg" borderWidth="1px" borderColor={borderColor}>
          <CardBody py={3}>
            <Flex justify="space-between" align="center">
              <HStack spacing={4}>
                <Checkbox
                  isChecked={selectedCount === unreadCount && unreadCount > 0}
                  isIndeterminate={
                    selectedCount > 0 && selectedCount < unreadCount
                  }
                  onChange={handleSelectAll}
                  colorScheme="blue"
                  size="lg"
                  isDisabled={unreadCount === 0}
                >
                  <Text fontWeight="medium">Select All Unread</Text>
                </Checkbox>
                {selectedCount > 0 && (
                  <Tag colorScheme="blue" size="lg" borderRadius="full" px={3}>
                    {selectedCount} selected
                  </Tag>
                )}
              </HStack>

              {selectedCount > 0 && (
                <Button
                  leftIcon={<IoCheckmarkDoneOutline />}
                  colorScheme="blue"
                  onClick={markSelectedAsRead}
                  size="sm"
                  isLoading={bulkLoading}
                  loadingText="Updating..."
                >
                  Mark as Read
                </Button>
              )}
            </Flex>
          </CardBody>
        </Card>
      )}

      {/* MAIN SCROLL CONTAINER FOR NOTIFICATIONS */}
      <Card
        shadow="lg"
        borderWidth="1px"
        borderColor={borderColor}
        height={{ base: '500px', md: '600px' }}
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        {notifications?.length === 0 ? (
          <CardBody
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            flex="1"
          >
            <Icon as={FiInbox} w={20} h={20} color="gray.300" mb={4} />
            <Heading size="md" mb={2} color="gray.500">
              No notifications
            </Heading>
            <Text color="gray.400" textAlign="center" mb={6}>
              You're all caught up! Check back later for updates.
            </Text>
            <Button
              leftIcon={<IoRefreshOutline />}
              variant="outline"
              onClick={handleRefresh}
            >
              Refresh
            </Button>
          </CardBody>
        ) : (
          <>
            {/* Scrollable Notifications Area */}
            <Box flex="1" overflowY="auto" p={4} sx={scrollbarStyles}>
              <VStack spacing={3} align="stretch">
                <Accordion allowMultiple onChange={handleAccordionChange}>
                  {notifications?.map((item) => {
                    const isUnread = !item.isRead;
                    const typeConfig = {
                      file_upload: { bg: 'blue.100', color: 'blue.500' },
                      file_edit: { bg: 'orange.100', color: 'orange.500' },
                      file_approved: { bg: 'green.100', color: 'green.500' },
                      file_rejected: { bg: 'red.100', color: 'red.500' },
                      file_processed: { bg: 'purple.100', color: 'purple.500' },
                      system_alert: { bg: 'yellow.100', color: 'yellow.600' },
                    }[item.type] || { bg: 'blue.100', color: 'blue.500' };

                    return (
                      <AccordionItem
                        key={item._id}
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="lg"
                        bg={isUnread ? unreadBg : bgColor}
                        mb={3}
                        boxShadow="sm"
                        overflow="hidden"
                      >
                        <h2>
                          <AccordionButton
                            _hover={{ bg: hoverBg }}
                            py={3}
                            px={4}
                          >
                            <Flex align="center" w="100%">
                              <Checkbox
                                isChecked={selectedIds.includes(item._id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelect(item._id);
                                }}
                                colorScheme="blue"
                                mr={4}
                                isDisabled={!isUnread}
                              />

                              <Flex flex="1" textAlign="left" align="center">
                                <Center
                                  bg={typeConfig.bg}
                                  p={1.5}
                                  borderRadius="md"
                                  mr={3}
                                >
                                  <Icon
                                    as={IoNotificationsOutline}
                                    color={typeConfig.color}
                                    w={4}
                                    h={4}
                                  />
                                </Center>

                                <Box flex="1">
                                  <HStack spacing={2} wrap="wrap">
                                    <Text
                                      fontWeight={isUnread ? '700' : '500'}
                                      fontSize="sm"
                                      color={titleColor}
                                      noOfLines={1}
                                    >
                                      {item.title || 'Update Notification'}
                                    </Text>
                                    {isUnread && (
                                      <Badge
                                        colorScheme="blue"
                                        borderRadius="full"
                                        px={2}
                                        fontSize="10px"
                                      >
                                        New
                                      </Badge>
                                    )}
                                    {/* Show recipient count badge for multi-recipient notifications */}
                                    {item.totalRecipients > 1 && (
                                      <Tooltip
                                        label={`Sent to ${item.totalRecipients} users`}
                                      >
                                        <Badge
                                          colorScheme="purple"
                                          variant="subtle"
                                          fontSize="10px"
                                        >
                                          <Flex align="center">
                                            <Icon as={IoPeopleOutline} mr={1} />
                                            {item.readCount || 0}/
                                            {item.totalRecipients}
                                          </Flex>
                                        </Badge>
                                      </Tooltip>
                                    )}
                                  </HStack>
                                  <HStack spacing={1} align="center" mt={1}>
                                    <Icon
                                      as={IoTimeOutline}
                                      fontSize="14px"
                                      color="gray.500"
                                    />
                                    <Text fontSize="xs" color="gray.500">
                                      {new Date(
                                        item.createdAt,
                                      ).toLocaleString()}
                                    </Text>
                                  </HStack>
                                </Box>
                              </Flex>
                            </Flex>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>

                        <AccordionPanel
                          pb={3}
                          px={4}
                          pt={2}
                          bg={panelBg}
                          borderTop="1px solid"
                          borderColor={borderColor}
                        >
                          <Divider mb={2} borderColor={borderColor} />

                          {/* Replace the Text component with a div that renders HTML */}
                          <Box
                            color={detailTextColor}
                            fontSize="sm"
                            lineHeight="tall"
                            dangerouslySetInnerHTML={{ __html: item.message }}
                          />

                          {/* Show read status if already read */}
                          {!isUnread && item.readAt && (
                            <Flex mt={3} align="center">
                              <Icon
                                as={IoCheckmarkDoneOutline}
                                color="green.500"
                                mr={2}
                              />
                              <Text fontSize="xs" color="green.600">
                                Read on {new Date(item.readAt).toLocaleString()}
                              </Text>
                            </Flex>
                          )}
                        </AccordionPanel>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </VStack>
            </Box>

            {/* Pagination - Fixed at bottom of card */}
            {totalPages > 1 && (
              <Box
                borderTop="1px solid"
                borderColor={borderColor}
                p={4}
                bg={cardBg}
              >
                <Flex
                  justify="space-between"
                  align="center"
                  wrap="wrap"
                  gap={4}
                >
                  <Text color="gray.500" fontSize="sm">
                    Showing {(page - 1) * limit + 1} to{' '}
                    {Math.min(page * limit, totalItems)} of {totalItems}{' '}
                    notifications
                  </Text>
                  {renderPagination()}
                </Flex>
              </Box>
            )}
          </>
        )}
      </Card>

      {bulkLoading && (
        <Box position="fixed" bottom="0" left="0" right="0">
          <Progress size="xs" isIndeterminate colorScheme="blue" />
        </Box>
      )}
    </Container>
  );
};

export default AllNotifications;
